import React, { useState, useRef, useCallback, useLayoutEffect, useEffect, memo } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Spacings, Text, COLORS } from '@styles'

const GAP = 8 // px between the trigger and the tooltip bubble
const ARROW = 8 // px size of the rotated-square arrow
const EDGE = 8 // px minimum distance the bubble keeps from the viewport edge

const TriggerWrapper = styled.span`
	display: ${({ $block }) => ($block ? 'block' : 'inline-flex')};
	width: ${({ $block }) => ($block ? '100%' : 'auto')};
	max-width: 100%;
	min-width: 0;
`

const Bubble = styled.div`
	position: fixed;
	top: ${({ $top }) => $top}px;
	left: ${({ $left }) => $left}px;
	z-index: 10000;
	max-width: 22rem;
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
	background: ${({ $variant, theme }) => ($variant === 'light' ? theme.colors.surface.card : COLORS.neutral[800])};
	color: ${({ $variant, theme }) => ($variant === 'light' ? theme.colors.text.primary : theme.colors.text.inverse)};
	border: ${({ $variant, theme }) => ($variant === 'light' ? `1px solid ${theme.colors.border.default}` : 'none')};
	font-family: ${({ theme }) => theme.fontFamily?.inter || 'Inter'}, sans-serif;
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	line-height: 1.25rem;
	text-align: left;
	border-radius: ${({ $variant, theme }) => ($variant === 'light' ? theme.radius.lg : theme.radius.sm)};
	box-shadow: ${({ theme }) => theme.shadows.md};
	white-space: normal;
	overflow-wrap: anywhere;
	pointer-events: none;
	visibility: ${({ $ready }) => ($ready ? 'visible' : 'hidden')};
`

const Arrow = styled.span`
	position: absolute;
	top: ${({ $top }) => $top}px;
	left: ${({ $left }) => $left}px;
	width: ${Spacings.SPACING_2B};
	height: ${Spacings.SPACING_2B};
	background: ${({ $variant, theme }) => ($variant === 'light' ? theme.colors.surface.card : COLORS.neutral[800])};
	border: ${({ $variant, theme }) => ($variant === 'light' ? `1px solid ${theme.colors.border.default}` : 'none')};
	z-index: ${({ $variant }) => ($variant === 'light' ? -1 : 'auto')};
	transform: rotate(45deg);
`

const INITIAL_POS = { top: 0, left: 0, arrowTop: 0, arrowLeft: 0, ready: false }

const Tooltip = ({
	content,
	children,
	placement = 'top',
	delay = 150,
	block = false,
	disabled = false,
	className,
	variant = 'dark',
	tapToggle = false,
}) => {
	const triggerRef = useRef(null)
	const bubbleRef = useRef(null)
	const showTimer = useRef(null)
	const [hoverOpen, setHoverOpen] = useState(false)
	const [clickOpen, setClickOpen] = useState(false)
	const [pos, setPos] = useState(INITIAL_POS)

	const isOpen = hoverOpen || (tapToggle && clickOpen)
	const active = !disabled && content !== null && content !== undefined && content !== ''

	const clearTimer = () => {
		if (showTimer.current) {
			clearTimeout(showTimer.current)
			showTimer.current = null
		}
	}

	const open = useCallback(() => {
		clearTimer()
		showTimer.current = setTimeout(() => setHoverOpen(true), delay)
	}, [delay])

	const close = useCallback(() => {
		clearTimer()
		setHoverOpen(false)
	}, [])

	const toggleClick = useCallback(() => {
		setClickOpen((v) => !v)
	}, [])

	// Reset the measured position only once both hover and click sources agree it's closed.
	useEffect(() => {
		if (!isOpen) setPos(INITIAL_POS)
	}, [isOpen])

	// Position the bubble once it is mounted, measuring both trigger and bubble.
	useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current || !bubbleRef.current) return
		const t = triggerRef.current.getBoundingClientRect()
		const b = bubbleRef.current.getBoundingClientRect()
		const vw = window.innerWidth
		const vh = window.innerHeight
		const isVertical = placement === 'top' || placement === 'bottom'

		let top
		let left
		if (placement === 'bottom') {
			top = t.bottom + GAP
			left = t.left + t.width / 2 - b.width / 2
		} else if (placement === 'left') {
			top = t.top + t.height / 2 - b.height / 2
			left = t.left - b.width - GAP
		} else if (placement === 'right') {
			top = t.top + t.height / 2 - b.height / 2
			left = t.right + GAP
		} else {
			top = t.top - b.height - GAP
			left = t.left + t.width / 2 - b.width / 2
		}

		// Keep the bubble inside the viewport.
		left = Math.max(EDGE, Math.min(left, vw - b.width - EDGE))
		top = Math.max(EDGE, Math.min(top, vh - b.height - EDGE))

		// Point the arrow back at the trigger centre, clamped to the bubble edges.
		let arrowTop
		let arrowLeft
		if (isVertical) {
			arrowLeft = Math.min(Math.max(t.left + t.width / 2 - left - ARROW / 2, ARROW), b.width - 2 * ARROW)
			arrowTop = placement === 'bottom' ? -ARROW / 2 : b.height - ARROW / 2
		} else {
			arrowTop = Math.min(Math.max(t.top + t.height / 2 - top - ARROW / 2, ARROW), b.height - 2 * ARROW)
			arrowLeft = placement === 'right' ? -ARROW / 2 : b.width - ARROW / 2
		}

		setPos({ top, left, arrowTop, arrowLeft, ready: true })
	}, [isOpen, placement, content])

	// Dismiss on scroll/resize so the bubble never lingers at a stale position.
	useEffect(() => {
		if (!isOpen) return undefined
		const onDismiss = () => {
			clearTimer()
			setHoverOpen(false)
			setClickOpen(false)
		}
		window.addEventListener('scroll', onDismiss, true)
		window.addEventListener('resize', onDismiss)
		return () => {
			window.removeEventListener('scroll', onDismiss, true)
			window.removeEventListener('resize', onDismiss)
		}
	}, [isOpen])

	// Tap-to-toggle: dismiss when the user taps/clicks anywhere outside the trigger.
	// No need to check the bubble itself — it's pointer-events:none, so it never receives the event.
	useEffect(() => {
		if (!tapToggle || !clickOpen) return undefined
		const onOutside = (e) => {
			if (triggerRef.current && !triggerRef.current.contains(e.target)) setClickOpen(false)
		}
		document.addEventListener('pointerdown', onOutside)
		return () => document.removeEventListener('pointerdown', onOutside)
	}, [tapToggle, clickOpen])

	useEffect(() => () => clearTimer(), [])

	if (!active) return children

	return (
		<>
			<TriggerWrapper
				ref={triggerRef}
				$block={block}
				className={className}
				onMouseEnter={open}
				onMouseLeave={close}
				onFocus={open}
				onBlur={close}
				onClick={tapToggle ? toggleClick : undefined}
			>
				{children}
			</TriggerWrapper>
			{isOpen &&
				typeof document !== 'undefined' &&
				createPortal(
					<Bubble ref={bubbleRef} role='tooltip' $top={pos.top} $left={pos.left} $ready={pos.ready} $variant={variant}>
						{content}
						<Arrow $top={pos.arrowTop} $left={pos.arrowLeft} $variant={variant} />
					</Bubble>,
					document.body
				)}
		</>
	)
}

Tooltip.displayName = 'Tooltip'

export default memo(Tooltip)
